import { ChangeDetectionStrategy, Component, InputSignal, OnInit, computed, inject, input, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';

import { NumberInputComponent } from '~components/form/number-input/number-input.component';
import { ErrorTooltipDirective } from '~core/directives/error-tooltip.directive';
import { GeneralDataTypeEnum } from '~core/models/enums/general-data-type.enum';
import { KeyValueModel } from '~core/models/key-value.model';
import { GeneralDataService } from '~core/services/general-data.service';
import { TranslationService } from '~core/services/translation.service';
import { OptionalType } from '~shared/core/types/optional.type';
import { QuantityUnitDisplayComponent } from '../../quantity-unit-display/quantity-unit-display.component';

@Component({
    selector: 'arc-currency-input',
    templateUrl: '../number-input/number-input.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: CurrencyInputComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        MatFormField,
        ErrorTooltipDirective,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        QuantityUnitDisplayComponent,
        MatSuffix,
        TranslocoPipe
    ]
})
export class CurrencyInputComponent extends NumberInputComponent implements OnInit {
    readonly currencyId = input<string>();
    readonly vatExclusive = input<boolean>();

    // fix decimalPlaces to 2
    override readonly decimalPlaces = computed(() => 2) as InputSignal<OptionalType<number>>;
    // fix unitQuantity to undefined
    override readonly unitQuantity = computed(() => undefined) as InputSignal<OptionalType<number>>;

    override readonly unitText = computed(() => {
        const currencyId = this.currencyId();
        let currency: string;

        if (!!currencyId) {
            currency = this.currencies().find(c => c.key === currencyId)?.value || '';
        } else {
            currency = 'CHF';
        }

        const exclVat = this.vatExclusive();
        if (exclVat === undefined) {
            return currency;
        }

        return `${currency} ${exclVat ? this.exclusiveSuffix() : this.inclusiveSuffix()}`;
    });

    override readonly unitTooltip = computed(() => this.unitText());

    override readonly _isCurrency = true;

    private readonly currencies = signal<KeyValueModel[]>([]);
    private readonly exclusiveSuffix = signal('');
    private readonly inclusiveSuffix = signal('');

    private readonly generalDataService = inject(GeneralDataService);
    private readonly vatTranslationService = inject(TranslationService);

    override ngOnInit(): void {
        super.ngOnInit();

        if (!!this.currencyId) {
            this.generalDataService.getGeneralData(GeneralDataTypeEnum.Currencies).subscribe(data => {
                this.currencies.set(data);
            });
        }

        const exclSub = this.vatTranslationService.getTextObservable('General.ExclVat').subscribe(v => this.exclusiveSuffix.set(v));
        const inclSub = this.vatTranslationService.getTextObservable('General.InclVat').subscribe(v => this.inclusiveSuffix.set(v));
        this.addSubscriptions(exclSub, inclSub);
    }
}
